import React, { useState, useEffect, useRef } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { pages } from "./pages";
import SidebarNavigation from "./components/navbar/SidebarNavigation";
import Headers from "./components/headers/Headers.component";
import DynamicParentPage from "./components/pages/DynamicParentPage/DynamicParentPage";
import AcccessDenied from "./components/accessDenied/AccessDenied.component";
import PageNotFound from "./components/pageNotFound/PageNotFound.component";
import { useUserContext } from "./context/UserContext";
import InactiveUser from "./components/inactiveUser/InactiveUser.component";
import UnProvisionedUser from "./components/unprovisionedUser/UnProvisionedUser.component";
import BreadcrumbsComponent from "./components/common/BreadCrums/Breadcrumbs.component";
import RightSidebarWithClocks from "./components/common/Clock/RightSidebarWithClocks";

const theme = createTheme({
  palette: {
    primary: {
      main: "#434343",
    },
    secondary: {
      main: "#f50057",
    },
    background: {
      default: "#f4f6f8",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: "ToyotaType, sans-serif",
  },
});

const App = () => {
  const [currentPage, setCurrentPage] = useState(
    window.location.pathname.substring(1) || "dashboard",
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const { userPageRoles, userPageRolesLoading, selectedUserDetail } =
    useUserContext();

  const { active } = selectedUserDetail || {};

  const sidebarRef = useRef(null);
  const menuButtonRef = useRef(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(window.location.pathname.substring(1) || "dashboard");
    };
    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setIsSidebarOpen(false);
      }
    };
    if (isSidebarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSidebarOpen]);

  const setPage = (path) => {
    window.history.pushState(null, "", `/${path}`);
    setCurrentPage(path);
  };

  const findPageByPath = (path, pagesArray) => {
    const findPageRecursively = (pages, targetPath) => {
      for (const page of pages) {
        if (page?.path === targetPath) {
          return page;
        }
        if (page?.subPages && page?.subPages?.length > 0) {
          const foundPage = findPageRecursively(page.subPages, targetPath);
          if (foundPage) {
            return foundPage;
          }
        }
      }
      return null;
    };
    return findPageRecursively(pagesArray, path);
  };

  const hasPermission = (page) => {
    if (!page.pageId) {
      return true;
    }
    return page.pageId.some((id) => userPageRoles[id] === true);
  };

  const filterPages = (pagesArray) => {
    const filterRecursively = (pages) => {
      return pages
        ?.map((page) => {
          if (page.subPages) {
            const filteredSubPages = filterRecursively(page.subPages);
            const isPermitted = hasPermission(page);

            if (isPermitted || filteredSubPages.length > 0) {
              return { ...page, subPages: filteredSubPages };
            }
            return null;
          }

          if (hasPermission(page)) {
            return page;
          }
          return null;
        })
        .filter(Boolean);
    };

    if (Object.keys(userPageRoles).length > 0) {
      return filterRecursively(pagesArray);
    }

    return pagesArray;
  };

  const renderContent = () => {
    const page = findPageByPath(currentPage, pages);
    if (!page) {
      return <PageNotFound />;
    }
    if (userPageRolesLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "90vh",
            flexDirection: "column",
            gap: "20px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <CircularProgress size={60} sx={{ color: "#f44336" }} />
          <Typography variant="h6" sx={{ mt: 2, color: "#434343" }}>
            Hang tight! We're almost there, getting the right pages for you.
          </Typography>
        </Box>
      );
    }

    const isParentWithEnabledChildren =
      page.subPages && filterPages([page]).length > 0;

    if (active === undefined) {
      return <UnProvisionedUser />;
    }
    if (!active) {
      return <InactiveUser />;
    } else if (hasPermission(page) || isParentWithEnabledChildren) {
      if (page?.component) {
        const Component = page.component;
        return <Component />;
      } else if (page?.subPages && page?.subPages?.length > 0) {
        const filteredParentPage = filterPages([page])[0];
        return (
          <DynamicParentPage
            parentPage={filteredParentPage}
            setPage={setPage}
          />
        );
      } else {
        return (
          <Box sx={{ p: 4 }}>
            <Typography variant="h5">No Content</Typography>
            <Typography variant="body1">
              This page has no component or visible sub-pages.
            </Typography>
          </Box>
        );
      }
    } else {
      return <AcccessDenied />;
    }
  };

  const filteredPages = active ? filterPages(pages) : [];

  return (
    <ThemeProvider theme={theme}>
      <Box className="flex w-full min-h-screen bg-gray-100 font-sans">
        <Headers
          setIsSidebarOpen={setIsSidebarOpen}
          isSidebarOpen={isSidebarOpen}
          menuButtonRef={menuButtonRef}
          handleMenu={handleMenu}
          anchorEl={anchorEl}
          handleClose={handleClose}
          setPage={setPage}
          showMenuIcon={
            !userPageRolesLoading &&
            userPageRoles &&
            Object.keys(userPageRoles)?.length > 0
          }
        />
        <div ref={sidebarRef}>
          <SidebarNavigation
            currentPage={currentPage}
            setPage={setPage}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            pages={filteredPages}
          />
        </div>

        <Box
          component="main"
          className="flex-1 p-4 sm:p-8 lg:p-12 transition-all duration-300 ease-in-out"
          sx={{
            ml: { sm: isSidebarOpen ? "250px" : "0" },
            mt: "100px",
          }}
        >
          <BreadcrumbsComponent currentPage={currentPage} setPage={setPage} />
          {renderContent()}
        </Box>
        <RightSidebarWithClocks />
      </Box>
    </ThemeProvider>
  );
};

export default App;
